import type { ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface StepperProps {
  id: string;
  label: string;
  /** El texto del borrador (se acepta lo tecleado tal cual). */
  value: string;
  onChange: (value: string) => void;
  step: number;
  min: number;
  max: number;
  decrementLabel: string;
  incrementLabel: string;
  placeholder?: string;
  inputMode?: 'numeric' | 'decimal';
  hint?: ReactNode;
  className?: string;
}

function parse(v: string): number | null {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
}

const btnCls =
  'inline-flex h-11 min-w-11 items-center justify-center rounded-[10px] border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] text-[var(--color-fg-primary)] transition-colors hover:bg-[var(--color-bg-elevated)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-primary)] disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none';

/**
 * − [número] +. El + y el − saltan a la rejilla del paso (⌊v/paso⌋·paso + paso); con el campo vacío o
 * ilegible el + arranca en el mayor de `min` y `step` y el − queda deshabilitado.
 */
export function Stepper({
  id,
  label,
  value,
  onChange,
  step,
  min,
  max,
  decrementLabel,
  incrementLabel,
  placeholder,
  inputMode = 'numeric',
  hint,
  className,
}: StepperProps) {
  const v = parse(value);
  const up = () => onChange(String(v == null ? Math.max(min, step) : Math.max(min, Math.min(max, Math.floor(v / step) * step + step))));
  const down = () => {
    if (v != null) onChange(String(Math.min(max, Math.max(min, Math.ceil(v / step) * step - step))));
  };
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label htmlFor={id} className="text-sm font-medium text-[var(--color-fg-primary)]">
        {label}
      </label>
      <div className="flex items-center gap-2.5">
        <button type="button" aria-label={decrementLabel} onClick={down} disabled={v == null || v <= min} className={btnCls}>
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          id={id}
          type="text"
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 w-28 min-w-0 rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-bg-input)] px-2 text-center font-[var(--font-dash-mono)] text-[26px] font-bold tabular-nums text-[var(--color-fg-primary)] focus:border-[var(--color-fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-fg-primary)]"
        />
        <button type="button" aria-label={incrementLabel} onClick={up} disabled={v != null && v >= max} className={btnCls}>
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {hint ? <span className="text-xs text-[var(--color-fg-muted)]">{hint}</span> : null}
    </div>
  );
}
